import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { createPlaygamaTopCategory } from '@/pages/Homepage/components/GamesBlock/TopPlaygama/prepare-top-playgama'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import type { ShortCategory } from '@/query/use-general-data'
import { useAppStore } from '@/store/app-store'
import type { APICategoryData, MainCategoriesType, ShortGameZipType } from '@/types'
import { zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { HOMEPAGE_CATEGORIES_CACHE } from '@/utils/requests/utils'

export type MainCategoryListZipType = Omit<ShortCategory, 'location'> & {
    location: RouteLocationNamedRaw | undefined
    games?: ShortGameZipType[]
}

export const useHomepageData = () => {
    const appStore = useAppStore()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getMainPageGamesByCategories, getMainPageTrending } = useAPI()

    // persister type depends on queryKey cardinality
    const queryKey = computed(() => [
        'homepage-data',
        appStore.platform,
        currentLocale.value,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.isDebugMode,
        appStore.contentScope,
        appStore.isNativeAppEnv,
        appStore.isNativeAndroidEnv,
    ])
    const persister = usePersisterFn(Persisters.LowCardinality)

    const highlightedHomepageCategories = ['trending_now', 'top_growth', 'top_playgama', 'new', 'casual-games']

    function mapCategoriesWithGames(categories: APICategoryData[]): MainCategoryListZipType[] {
        const result = categories
            .map(({ id, hru, title, games, categoryType }) => ({
                id: id as MainCategoriesType,
                title,
                location: getCategoryRoute({ hru, type: categoryType }),
                games: games.map(zipAPIGame),
            }))
            .filter(({ games }) => games.length > 0)

        return [...result, createPlaygamaTopCategory(appStore.isNativeAppEnv, appStore.isNativeAndroidEnv)]
    }
    const { data, suspense } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await getMainPageGamesByCategories({
                platform: appStore.platform,
                locale: currentLocale.value,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                withDebugInfo: appStore.isDebugMode,
                scope: appStore.contentScope,
            })
            if (result.status !== 'ok') {
                throw new Error(result.status === 'error' ? result.message : `HTTP ${result.httpStatus}`)
            }

            return mapCategoriesWithGames(result.data.categories)
        },
        staleTime: HOMEPAGE_CATEGORIES_CACHE,
        persister,
    })

    const trendingQueryKey = computed(() => [
        'homepage-trending',
        appStore.platform,
        currentLocale.value,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.isDebugMode,
        appStore.contentScope,
        appStore.clid,
    ])
    const { data: trendingData, suspense: trendingSuspense } = useQuery({
        queryKey: trendingQueryKey,
        queryFn: async () => {
            const result = await getMainPageTrending({
                platform: appStore.platform,
                locale: currentLocale.value,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                withDebugInfo: appStore.isDebugMode,
                scope: appStore.contentScope,
                clid: appStore.clid,
            })
            const trending = result.status === 'ok' ? result.data.categories[0] : undefined

            // null keeps the main page block: a failed ranking must not break the page
            return trending?.games.length ? { games: trending.games.map(zipAPIGame), slateId: trending.slateId } : null
        },
        // The ranked block is user-specific and must never enter the SSR persister cache.
        persister: undefined,
    })

    const categoriesWithGames = computed(() =>
        data.value?.map((category) =>
            category.id === 'trending_now' && trendingData.value
                ? { ...category, games: trendingData.value.games }
                : category,
        ),
    )

    return {
        categoriesWithGames,
        trendingSlateId: computed(() => trendingData.value?.slateId),
        suspense: () => Promise.all([suspense(), trendingSuspense()]),
        highlightedHomepageCategories,
    }
}
